import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { 
  Menu, X, ChevronRight, ChevronDown, Users, DollarSign, Settings, Headphones,
  Cpu, Briefcase, Building2, ArrowRight, Check, Phone, Mail, MapPin,
  TrendingUp, Clock, Award, Shield, Target, Zap, Globe, BarChart3,
  FileText, Pencil, Trash2, Plus, LogOut, Eye, EyeOff, Search, Database,
  UserCheck, LineChart, ClipboardCheck, FolderKanban, Workflow, CalendarCheck
} from 'lucide-react';
import '@/App.css';

// Import SEO Pages
import { 
  SolutionsIndexPage, 
  SolutionPage, 
  LocationsIndexPage, 
  LocationPage,
  solutionPages,
  usLocations,
  auLocations
} from './seoPages';

// Import SEO Content
import { serviceContent, serviceImages, industryContent, industryImages } from './seoContent';

// Import Blog
import { BlogIndexPage, BlogPostPage } from './blogData';

// Import Internal Links
import InternalLinks from './InternalLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== SHARED DATA ==============

// All 12 services organized by category
const services = [
  // Core Business Functions
  { 
    id: 'hr', 
    title: 'HR Operations', 
    icon: Users, 
    path: '/services/hr',
    category: 'core',
    shortDesc: 'Recruitment support, onboarding, payroll administration, compliance coordination',
    fullDesc: 'Build a dedicated HR team to support your recruitment, onboarding, and employee lifecycle processes.',
    capabilities: ['Recruitment coordination', 'Resume screening', 'Interview scheduling', 'Onboarding administration', 'Payroll support', 'HR reporting'],
    benefits: ['Reduce HR overhead', 'Improve hiring speed', 'Maintain compliance'],
    cta: 'Build Your HR Team',
    seoTitle: 'HR Operations Support | APAC Center of Excellence',
    seoDescription: 'Offshore HR operations support including recruitment, onboarding, payroll administration, and compliance coordination for growing companies.',
    relatedServices: ['recruitment', 'operations', 'compliance-support']
  },
  { 
    id: 'finance', 
    title: 'Finance & Accounting', 
    icon: DollarSign, 
    path: '/services/finance',
    category: 'core',
    shortDesc: 'Accounts payable/receivable, reconciliations, reporting, bookkeeping',
    fullDesc: 'Build a dedicated finance team handling accounts payable/receivable, monthly reconciliations, and financial reporting.',
    capabilities: ['Accounts payable/receivable', 'Bank reconciliations', 'Financial reporting', 'Bookkeeping', 'Data entry'],
    benefits: ['Lower finance costs', 'Improve accuracy', 'Faster reporting cycles'],
    cta: 'Get Finance Support',
    seoTitle: 'Finance & Accounting Support | APAC Center of Excellence',
    seoDescription: 'Offshore finance and accounting support including AP/AR, reconciliations, reporting, and bookkeeping for growing companies.',
    relatedServices: ['fpa', 'data-services', 'operations']
  },
  { 
    id: 'operations', 
    title: 'Business Operations', 
    icon: Settings, 
    path: '/services/operations',
    category: 'core',
    shortDesc: 'Back-office support, workflow management, reporting, admin functions',
    fullDesc: 'Scale your operations with dedicated back-office support, workflow management, and administrative functions.',
    capabilities: ['Administrative support', 'Data processing', 'Workflow coordination', 'Reporting', 'CRM updates'],
    benefits: ['Streamline workflows', 'Reduce overhead', 'Scale efficiently'],
    cta: 'Scale Your Operations',
    seoTitle: 'Business Operations Support | APAC Center of Excellence',
    seoDescription: 'Offshore business operations support including back-office, workflow management, and admin functions for growing companies.',
    relatedServices: ['process-optimization', 'data-services', 'project-management']
  },
  { 
    id: 'customer-support', 
    title: 'Customer Support', 
    icon: Headphones, 
    path: '/services/customer-support',
    category: 'core',
    shortDesc: 'Email, chat, and voice-based customer support aligned to your standards',
    fullDesc: 'Deliver exceptional customer experiences with dedicated support teams aligned to US standards.',
    capabilities: ['Email support', 'Live chat support', 'Voice support', 'Ticket management', 'Customer feedback analysis'],
    benefits: ['24/7 coverage options', 'Globally-aligned service', 'Scalable support'],
    cta: 'Build Your Support Team',
    seoTitle: 'Customer Support Services | APAC Center of Excellence',
    seoDescription: 'Offshore customer support services including email, chat, and voice support aligned to your standards.',
    relatedServices: ['sales-support', 'operations', 'data-services']
  },
  // Growth & Revenue Support
  { 
    id: 'recruitment', 
    title: 'Recruitment', 
    icon: Search, 
    path: '/services/recruitment',
    category: 'growth',
    shortDesc: 'Candidate sourcing, screening, scheduling, and hiring coordination',
    fullDesc: 'Build a scalable recruitment support function to help your team move faster, improve candidate flow, and reduce administrative hiring bottlenecks.',
    capabilities: ['Candidate sourcing support', 'Resume screening', 'Interview scheduling', 'Applicant tracking system updates', 'Candidate communications', 'Talent pipeline administration'],
    benefits: ['Faster hiring processes', 'Lower recruiting overhead', 'Better candidate coordination', 'More scalable talent operations'],
    cta: 'Build Your Recruitment Team',
    seoTitle: 'Recruitment Support Services | APAC Center of Excellence',
    seoDescription: 'Offshore recruitment support including candidate sourcing, screening, scheduling, and hiring coordination for growing companies.',
    relatedServices: ['hr', 'sales-support', 'operations']
  },
  { 
    id: 'sales-support', 
    title: 'Sales Support', 
    icon: TrendingUp, 
    path: '/services/sales-support',
    category: 'growth',
    shortDesc: 'Lead management, CRM hygiene, reporting, and sales administration',
    fullDesc: 'Support your revenue team with structured offshore assistance across lead management, CRM hygiene, reporting, and sales administration.',
    capabilities: ['CRM updates', 'Lead list management', 'Pipeline reporting', 'Proposal support', 'Sales admin support', 'Meeting coordination', 'Follow-up workflows'],
    benefits: ['Better CRM discipline', 'Improved sales team efficiency', 'More time for revenue-generating activity', 'Stronger pipeline visibility'],
    cta: 'Support Your Sales Team',
    seoTitle: 'Sales Support Services | APAC Center of Excellence',
    seoDescription: 'Offshore sales support including lead management, CRM hygiene, pipeline reporting, and sales administration for growing companies.',
    relatedServices: ['recruitment', 'data-services', 'customer-support']
  },
  // Administrative & Data Services
  { 
    id: 'executive-assistants', 
    title: 'Executive Assistants', 
    icon: CalendarCheck, 
    path: '/services/executive-assistants',
    category: 'admin',
    shortDesc: 'Calendar management, inbox support, travel coordination, meeting scheduling',
    fullDesc: 'Extend the productivity of your leadership team with dedicated executive and administrative support.',
    capabilities: ['Calendar management', 'Inbox support', 'Travel coordination', 'Meeting scheduling', 'Document preparation', 'Research assistance', 'Follow-up tracking'],
    benefits: ['Increased executive productivity', 'Better organization and responsiveness', 'Reduced administrative distraction', 'More capacity for strategic work'],
    cta: 'Add Executive Support',
    seoTitle: 'Executive Assistant Support | APAC Center of Excellence',
    seoDescription: 'Offshore executive assistant support including calendar management, inbox support, travel coordination, and meeting scheduling.',
    relatedServices: ['operations', 'project-management', 'data-services']
  },
  { 
    id: 'data-services', 
    title: 'Data Services', 
    icon: Database, 
    path: '/services/data-services',
    category: 'admin',
    shortDesc: 'Data entry, cleansing, enrichment, CRM updates, and reporting support',
    fullDesc: 'Improve data quality, reduce manual workload, and keep business systems up to date with dedicated offshore data support.',
    capabilities: ['Data entry', 'Data cleansing', 'Data enrichment', 'CRM updates', 'Spreadsheet maintenance', 'Database administration support', 'Reporting support'],
    benefits: ['Improved data accuracy', 'Faster processing times', 'Reduced admin burden', 'Better reporting reliability'],
    cta: 'Get Data Support',
    seoTitle: 'Data Services | APAC Center of Excellence',
    seoDescription: 'Offshore data services including data entry, cleansing, enrichment, CRM updates, and reporting support for growing companies.',
    relatedServices: ['operations', 'finance', 'sales-support']
  },
  // Specialized Support
  { 
    id: 'fpa', 
    title: 'FP&A', 
    icon: LineChart, 
    path: '/services/fpa',
    category: 'specialized',
    shortDesc: 'Budgeting, forecasting, management reporting, and KPI tracking',
    fullDesc: 'Strengthen financial visibility with offshore support for budgeting, forecasting, management reporting, and KPI tracking.',
    capabilities: ['Budget support', 'Forecast preparation', 'KPI dashboards', 'Management reporting', 'Financial analysis support', 'Variance tracking'],
    benefits: ['Better decision support', 'Faster reporting cycles', 'Improved financial visibility', 'Lower cost finance capability'],
    cta: 'Strengthen Financial Planning',
    seoTitle: 'FP&A Support Services | APAC Center of Excellence',
    seoDescription: 'Offshore FP&A support including budgeting, forecasting, management reporting, and KPI tracking for growing companies.',
    relatedServices: ['finance', 'data-services', 'operations']
  },
  { 
    id: 'compliance-support', 
    title: 'Compliance Support', 
    icon: ClipboardCheck, 
    path: '/services/compliance-support',
    category: 'specialized',
    shortDesc: 'Documentation, tracking, audit preparation, and policy administration',
    fullDesc: 'Reduce administrative pressure around documentation, tracking, and internal compliance workflows.',
    capabilities: ['Document preparation', 'Compliance tracking support', 'Audit readiness support', 'Policy administration', 'Record maintenance', 'Internal reporting support'],
    benefits: ['Better documentation discipline', 'Reduced compliance admin workload', 'Improved internal visibility', 'Stronger operational consistency'],
    cta: 'Get Compliance Support',
    seoTitle: 'Compliance Support Services | APAC Center of Excellence',
    seoDescription: 'Offshore compliance support including documentation, tracking, audit preparation, and policy administration for growing companies.',
    relatedServices: ['hr', 'finance', 'operations']
  },
  { 
    id: 'project-management', 
    title: 'Project Management', 
    icon: FolderKanban, 
    path: '/services/project-management',
    category: 'specialized',
    shortDesc: 'Project coordination, status reporting, action tracking, scheduling',
    fullDesc: 'Improve project coordination, reporting, and execution with dedicated offshore support for project teams and business functions.',
    capabilities: ['Project coordination', 'Status reporting', 'Action tracking', 'Scheduling support', 'Resource coordination', 'Documentation support', 'Meeting administration'],
    benefits: ['Better project visibility', 'Improved execution discipline', 'Reduced coordination burden', 'More consistent follow-through'],
    cta: 'Scale Project Delivery',
    seoTitle: 'Project Management Support | APAC Center of Excellence',
    seoDescription: 'Offshore project management support including coordination, reporting, tracking, and scheduling for growing companies.',
    relatedServices: ['operations', 'process-optimization', 'executive-assistants']
  },
  { 
    id: 'process-optimization', 
    title: 'Process Optimization', 
    icon: Workflow, 
    path: '/services/process-optimization',
    category: 'specialized',
    shortDesc: 'SOP documentation, workflow mapping, process gap identification',
    fullDesc: 'Document, improve, and standardize key workflows to increase efficiency and support scalable growth.',
    capabilities: ['SOP documentation', 'Workflow mapping', 'Process gap identification', 'Operational improvement support', 'Documentation standardization', 'Internal coordination support'],
    benefits: ['Better operational consistency', 'Clearer internal processes', 'Reduced inefficiencies', 'Easier scaling across teams'],
    cta: 'Improve Your Processes',
    seoTitle: 'Process Optimization Support | APAC Center of Excellence',
    seoDescription: 'Offshore process optimization support including SOP documentation, workflow mapping, and process improvement for growing companies.',
    relatedServices: ['operations', 'project-management', 'compliance-support']
  }
];

// Service categories for grouping
const serviceCategories = [
  { id: 'core', title: 'Core Business Functions', description: 'Essential operational support for day-to-day business functions' },
  { id: 'growth', title: 'Growth & Revenue Support', description: 'Support functions that drive business growth and revenue' },
  { id: 'admin', title: 'Administrative & Data Services', description: 'High-quality administrative and data management support' },
  { id: 'specialized', title: 'Specialized Support', description: 'Expert support for specialized business needs' }
];

// Featured services for homepage (8 services)
const featuredServiceIds = ['hr', 'finance', 'operations', 'customer-support', 'recruitment', 'data-services', 'executive-assistants', 'sales-support'];
const featuredServices = services.filter(s => featuredServiceIds.includes(s.id));

// All service options for contact form dropdown
const serviceOptions = [
  'HR Operations',
  'Finance & Accounting',
  'Business Operations',
  'Customer Support',
  'Recruitment',
  'Data Services',
  'Executive Assistant Support',
  'Sales Support',
  'FP&A Support',
  'Compliance Support',
  'Project Management Support',
  'Process Optimization Support',
  'Not Sure Yet'
];

const industries = [
  {
    id: 'technology',
    title: 'Technology',
    icon: Cpu,
    path: '/industries/technology',
    challenges: ['Rapid scaling needs', 'High operational costs', 'Administrative bottlenecks'],
    solutions: ['HR and recruitment support', 'Finance operations', 'Back-office support']
  },
  {
    id: 'staffing',
    title: 'Staffing',
    icon: Briefcase,
    path: '/industries/staffing',
    challenges: ['High candidate volumes', 'Administrative burden', 'Margin pressure'],
    solutions: ['Resume screening', 'Interview coordination', 'Payroll support']
  },
  {
    id: 'financial-services',
    title: 'Financial Services',
    icon: Building2,
    path: '/industries/financial-services',
    challenges: ['Compliance requirements', 'Reporting complexity', 'Cost pressure'],
    solutions: ['Data processing', 'Reporting support', 'Back-office operations']
  }
];

const processSteps = [
  { step: 1, title: 'Define Requirements', description: 'We assess your needs and define roles' },
  { step: 2, title: 'Build Your Team', description: 'We source and vet candidates' },
  { step: 3, title: 'Train & Integrate', description: 'We align with your systems and processes' },
  { step: 4, title: 'Manage & Optimize', description: 'We provide ongoing management and reporting' }
];

const whyApacPoints = [
  { icon: TrendingUp, title: '40-60% Cost Savings', description: 'Significant reduction in operational costs' },
  { icon: Award, title: 'Highly Skilled Talent', description: 'Access to trained professionals' },
  { icon: Zap, title: 'Scalable Teams', description: 'Grow your team as needed' },
  { icon: Shield, title: 'Client-Aligned Processes', description: 'Seamless integration with your workflows' }
];

const proofMetrics = [
  { value: '60%', label: 'Cost Reduction', sublabel: 'Average savings for clients' },
  { value: '30', label: 'Days to Deploy', sublabel: 'Get your team operational fast' },
  { value: '95%', label: 'Retention Rate', sublabel: 'Teams that stay and grow with you' }
];

// Helper function to get related services
const getRelatedServices = (serviceId) => {
  const service = services.find(s => s.id === serviceId);
  if (!service || !service.relatedServices) return [];
  return service.relatedServices.map(id => services.find(s => s.id === id)).filter(Boolean);
};

// ============== COMPONENTS ==============

// SEO Head Component
const SEOHead = ({ title, description }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
  </Helmet>
);

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
  }, [location]);

  return (
    <nav className="glass sticky top-0 z-50 border-b border-gray-200/50" data-testid="navbar">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-10 h-10 bg-[#0B1F3A] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="font-semibold text-[#0B1F3A] hidden sm:block">APAC COE</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5">
            {/* Services Mega Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-[#0B1F3A] hover:text-[#2F80ED] transition-colors"
                data-testid="nav-services-dropdown"
              >
                Services <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-6 w-[600px]">
                  <Link to="/services" className="block text-[#2F80ED] font-medium mb-4 hover:underline" data-testid="nav-services-all">
                    View All Services →
                  </Link>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                    {services.map(s => (
                      <Link 
                        key={s.id} 
                        to={s.path} 
                        className="flex items-center gap-2 py-1.5 text-[#0B1F3A] hover:text-[#2F80ED] transition-colors text-sm" 
                        data-testid={`nav-service-${s.id}`}
                      >
                        <s.icon className="w-4 h-4 text-[#2F80ED]" />
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Industries Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-[#0B1F3A] hover:text-[#2F80ED] transition-colors"
                data-testid="nav-industries-dropdown"
              >
                Industries <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[200px]">
                  <Link to="/industries" className="block px-4 py-2 hover:bg-gray-50 text-[#0B1F3A]" data-testid="nav-industries-all">All Industries</Link>
                  {industries.map(i => (
                    <Link key={i.id} to={i.path} className="block px-4 py-2 hover:bg-gray-50 text-[#0B1F3A]" data-testid={`nav-industry-${i.id}`}>
                      {i.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/how-it-works" className="text-[#0B1F3A] hover:text-[#2F80ED] transition-colors" data-testid="nav-how-it-works">How It Works</Link>
            <Link to="/solutions" className="text-[#0B1F3A] hover:text-[#2F80ED] transition-colors" data-testid="nav-solutions">Solutions</Link>
            <Link to="/locations" className="text-[#0B1F3A] hover:text-[#2F80ED] transition-colors" data-testid="nav-locations">Locations</Link>
            
            {/* Resources Dropdown (Case Studies + Blog) */}
            <div className="relative group">
              <button 
                className="flex items-center gap-1 text-[#0B1F3A] hover:text-[#2F80ED] transition-colors"
                data-testid="nav-resources-dropdown"
              >
                Resources <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[200px]">
                  <Link to="/case-studies" className="block px-4 py-2 hover:bg-gray-50 text-[#0B1F3A]" data-testid="nav-case-studies">Case Studies</Link>
                  <Link to="/blog" className="block px-4 py-2 hover:bg-gray-50 text-[#0B1F3A]" data-testid="nav-blog">Blog</Link>
                </div>
              </div>
            </div>

            <Link to="/about" className="text-[#0B1F3A] hover:text-[#2F80ED] transition-colors" data-testid="nav-about">About</Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact" className="btn-secondary text-sm py-2 px-4" data-testid="nav-contact-btn">Contact Us</Link>
            <Link to="/contact?type=book_call" className="btn-primary text-sm py-2 px-4" data-testid="nav-book-call-btn">Book a Call</Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-fade-in max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="space-y-2">
              {/* Services */}
              <div>
                <button 
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center justify-between w-full py-2 text-[#0B1F3A] font-medium"
                >
                  Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="pl-4 space-y-1 mt-2 border-l-2 border-gray-100">
                    <Link to="/services" className="block py-1.5 text-[#2F80ED] font-medium">All Services</Link>
                    {services.map(s => (
                      <Link key={s.id} to={s.path} className="block py-1.5 text-gray-600">{s.title}</Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries */}
              <div>
                <button 
                  onClick={() => setIndustriesOpen(!industriesOpen)}
                  className="flex items-center justify-between w-full py-2 text-[#0B1F3A] font-medium"
                >
                  Industries <ChevronDown className={`w-4 h-4 transition-transform ${industriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {industriesOpen && (
                  <div className="pl-4 space-y-1 mt-2 border-l-2 border-gray-100">
                    <Link to="/industries" className="block py-1.5 text-gray-600">All Industries</Link>
                    {industries.map(i => (
                      <Link key={i.id} to={i.path} className="block py-1.5 text-gray-600">{i.title}</Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/how-it-works" className="block py-2 text-[#0B1F3A]">How It Works</Link>
              <Link to="/solutions" className="block py-2 text-[#0B1F3A]">Solutions</Link>
              <Link to="/locations" className="block py-2 text-[#0B1F3A]">Locations</Link>
              <Link to="/case-studies" className="block py-2 text-[#0B1F3A]">Case Studies</Link>
              <Link to="/blog" className="block py-2 text-[#0B1F3A]">Blog</Link>
              <Link to="/about" className="block py-2 text-[#0B1F3A]">About</Link>
              <Link to="/contact" className="block py-2 text-[#0B1F3A]">Contact</Link>

              <div className="pt-4 space-y-2">
                <Link to="/contact?type=book_call" className="btn-primary w-full text-center">Book a Call</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-[#0B1F3A] text-white py-16" data-testid="footer">
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
        {/* Brand */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[#2F80ED] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="font-semibold">APAC Center of Excellence</span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
            Singapore-based BPO provider delivering dedicated offshore staff in the Philippines and Mexico. HR outsourcing, finance & accounting, recruitment, and business operations support.
          </p>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/services" className="hover:text-white transition-colors">All Services</Link></li>
            {services.filter(s => s.category === 'core').map(s => (
              <li key={s.id}>
                <Link to={s.path} className="hover:text-white transition-colors">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Solutions */}
        <div>
          <h4 className="font-semibold mb-4">Resources</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/solutions" className="hover:text-white transition-colors">All Solutions</Link></li>
            <li><Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            <li><Link to="/industries" className="hover:text-white transition-colors">Industries</Link></li>
            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
          </ul>
        </div>

        {/* Locations */}
        <div>
          <h4 className="font-semibold mb-4">Locations</h4>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li><Link to="/locations" className="hover:text-white transition-colors">All Locations</Link></li>
            <li><Link to="/locations/new-york" className="hover:text-white transition-colors">New York</Link></li>
            <li><Link to="/locations/san-francisco" className="hover:text-white transition-colors">San Francisco</Link></li>
            <li><Link to="/locations/chicago" className="hover:text-white transition-colors">Chicago</Link></li>
            <li><Link to="/locations/sydney" className="hover:text-white transition-colors">Sydney</Link></li>
            <li><Link to="/locations/melbourne" className="hover:text-white transition-colors">Melbourne</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-semibold mb-4">Contact</h4>
          <ul className="space-y-3 text-gray-400 text-sm">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:info@apaccoe.com" className="hover:text-white transition-colors">info@apaccoe.com</a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>USA: +1 (844) 625-8400</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>Australia: (08) 9930 0000</span>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>Singapore (HQ), Philippines, Mexico</span>
            </li>
          </ul>
          <div className="mt-4 space-y-2">
            <Link to="/contact" className="text-[#2F80ED] hover:underline text-sm block">Contact Us →</Link>
            <Link to="/pricing" className="text-[#2F80ED] hover:underline text-sm block">View Pricing →</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800 mt-6 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} APAC Centre of Excellence Pte Ltd. All rights reserved.</p>
        <div className="flex gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
        </div>
      </div>
    </div>
  </footer>
);

// CTA Banner Component
const CTABanner = ({ title = "Build Your Offshore BPO Team Today", buttonText = "Book a Call", dark = false }) => (
  <section className={`section ${dark ? 'bg-[#0B1F3A]' : 'section-surface'}`} data-testid="cta-banner">
    <div className="container mx-auto text-center">
      <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${dark ? 'text-white' : 'text-[#0B1F3A]'}`}>
        {title}
      </h2>
      <Link 
        to="/contact?type=book_call" 
        className={dark ? 'btn-primary bg-[#2F80ED]' : 'btn-primary'}
        data-testid="cta-book-call-btn"
      >
        {buttonText} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

// Related Services Component
const RelatedServices = ({ serviceId }) => {
  const related = getRelatedServices(serviceId);
  if (related.length === 0) return null;

  return (
    <section className="section section-surface">
      <div className="container mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-8 text-center">Related Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6  mx-auto">
          {related.map(service => (
            <Link key={service.id} to={service.path} className="card p-6 text-center" data-testid={`related-service-${service.id}`}>
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <service.icon className="w-6 h-6 text-[#2F80ED]" />
              </div>
              <h3 className="font-semibold text-[#0B1F3A] mb-2">{service.title}</h3>
              <p className="text-sm text-[#475569] mb-3">{service.shortDesc}</p>
              <span className="text-[#2F80ED] text-sm font-medium inline-flex items-center gap-1">
                Learn more <ChevronRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Component
const ContactForm = ({ formType = 'contact', serviceInterest = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: serviceInterest,
    message: '',
    form_type: formType
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: 'Thank you! We\'ll be in touch shortly.' });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: serviceInterest,
        message: '',
        form_type: formType
      });
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Name *</label>
          <input
            type="text"
            required
            className="form-input"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Your name"
            data-testid="form-name-input"
          />
        </div>
        <div>
          <label className="form-label">Email *</label>
          <input
            type="email"
            required
            className="form-input"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="you@company.com"
            data-testid="form-email-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">Company</label>
          <input
            type="text"
            className="form-input"
            value={formData.company}
            onChange={(e) => setFormData({...formData, company: e.target.value})}
            placeholder="Company name"
            data-testid="form-company-input"
          />
        </div>
        <div>
          <label className="form-label">Phone</label>
          <input
            type="tel"
            className="form-input"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="+1 (555) 000-0000"
            data-testid="form-phone-input"
          />
        </div>
      </div>
      <div>
        <label className="form-label">Service Needed</label>
        <select
          className="form-input"
          value={formData.service_interest}
          onChange={(e) => setFormData({...formData, service_interest: e.target.value})}
          data-testid="form-service-select"
        >
          <option value="">Select a service</option>
          {serviceOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-label">Message *</label>
        <textarea
          required
          className="form-input form-textarea"
          value={formData.message}
          onChange={(e) => setFormData({...formData, message: e.target.value})}
          placeholder="Tell us about your needs..."
          data-testid="form-message-textarea"
        />
      </div>
      
      {status.message && (
        <div 
          className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
          data-testid="form-status-message"
        >
          {status.message}
        </div>
      )}
      
      <button 
        type="submit" 
        className="btn-primary w-full"
        disabled={isSubmitting}
        data-testid="form-submit-btn"
      >
        {isSubmitting ? 'Sending...' : formType === 'book_call' ? 'Request a Call' : 'Send Message'}
      </button>
    </form>
  );
};

// ============== PAGES ==============

// Homepage
const HomePage = () => (
  <div data-testid="home-page">
    <SEOHead 
      title="APAC Center of Excellence | Business Process Outsourcing (BPO) | Offshore Staff Philippines & Mexico"
      description="Singapore-based BPO provider delivering dedicated offshore staff in the Philippines and Mexico. HR outsourcing, finance & accounting BPO, recruitment process outsourcing, and business operations support for companies worldwide."
    />
    
    {/* Hero Section */}
    <section className="section bg-white relative overflow-hidden">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div className="animate-fade-in-up">
            <p className="overline mb-4">Business Process Outsourcing</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tighter text-[#0B1F3A] mb-6">
              Dedicated Offshore Staff for Your Business
            </h1>
            <p className="text-lg text-[#475569] mb-8 leading-relaxed">
              Scale your HR, finance, and operations with dedicated BPO teams in the Philippines and Mexico. Reduce costs by up to 60% with fully managed offshore staffing solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?type=book_call" className="btn-primary" data-testid="hero-book-call-btn">
                Book a Call <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="btn-secondary" data-testid="hero-pricing-btn">
                Get Pricing
              </Link>
            </div>
          </div>
          <div className="animate-fade-in-up animation-delay-200">
            <img 
              src="https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NDkyMTQyOHww&ixlib=rb-4.1.0&q=85"
              alt="Modern corporate office"
              className="rounded-xl shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Services Section - Updated with 8 Featured Services */}
    <section className="section section-surface" data-testid="services-section">
      <div className="container mx-auto">
        <div className="text-center mb-6">
          <p className="overline mb-4">What We Do</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B1F3A] mb-4">Our Services</h2>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto">
            Flexible offshore support across core operations, growth functions, and specialized business services.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredServices.map((service, idx) => (
            <Link 
              key={service.id} 
              to={service.path}
              className={`card p-6 animate-fade-in-up animation-delay-${((idx % 4) + 1) * 100}`}
              data-testid={`service-card-${service.id}`}
            >
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-[#2F80ED]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0B1F3A] mb-2">{service.title}</h3>
              <p className="text-[#475569] text-sm mb-4 line-clamp-2">{service.shortDesc}</p>
              <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1 text-sm">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
        <div className="text-center mt-5">
          <Link to="/services" className="btn-outline" data-testid="view-all-services-btn">
            View All 12 Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* Industries Section */}
    <section className="section" data-testid="industries-section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">Who We Work With</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B1F3A]">Industries We Serve</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {industries.map((industry, idx) => (
            <Link 
              key={industry.id}
              to={industry.path}
              className={`card p-8 text-center animate-fade-in-up animation-delay-${(idx + 1) * 100}`}
              data-testid={`industry-card-${industry.id}`}
            >
              <div className="w-16 h-16 bg-[#0B1F3A] rounded-xl flex items-center justify-center mx-auto mb-6">
                <industry.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0B1F3A] mb-4">{industry.title}</h3>
              <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1">
                Explore <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* How It Works */}
    <section className="section section-surface" data-testid="how-it-works-section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">The Process</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B1F3A]">How It Works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {processSteps.map((step, idx) => (
            <div key={step.step} className={`text-center animate-fade-in-up animation-delay-${(idx + 1) * 100}`}>
              <div className="w-14 h-14 bg-[#2F80ED] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                {step.step}
              </div>
              <h3 className="text-xl font-semibold text-[#0B1F3A] mb-2">{step.title}</h3>
              <p className="text-[#475569]">{step.description}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-6">
          <Link to="/how-it-works" className="btn-outline" data-testid="how-it-works-learn-more">
            Learn More <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* Why APAC */}
    <section className="section" data-testid="why-apac-section">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="overline mb-4">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0B1F3A] mb-8">Why APAC?</h2>
            <div className="space-y-6">
              {whyApacPoints.map((point, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <point.icon className="w-6 h-6 text-[#2F80ED]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0B1F3A] mb-1">{point.title}</h4>
                    <p className="text-[#475569]">{point.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1758518727193-a8da31447858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBvZmZpY2V8ZW58MHx8fHwxNzc0OTIxNDI5fDA&ixlib=rb-4.1.0&q=85"
              alt="Business meeting"
              className="rounded-xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Proof Metrics */}
    <section className="section bg-[#0B1F3A]" data-testid="metrics-section">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {proofMetrics.map((metric, idx) => (
            <div key={idx} className="animate-fade-in-up">
              <div className="text-5xl md:text-6xl font-bold text-[#2F80ED] mb-2">
                {metric.value}{metric.value !== '95%' && metric.value !== '60%' ? '' : ''}
              </div>
              <div className="text-xl font-semibold text-white mb-1">{metric.label}</div>
              <div className="text-gray-400">{metric.sublabel}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <CTABanner />
  </div>
);

// Services Page - Updated with Categories
const ServicesPage = () => (
  <div data-testid="services-page">
    <SEOHead 
      title="Our Services | APAC Center of Excellence"
      description="Explore our full range of offshore business services including HR, Finance, Operations, Recruitment, Data Services, Executive Assistants, and more."
    />
    
    <section className="section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">What We Offer</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">Our Services</h1>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto">
            We help companies scale efficiently with dedicated APAC-based teams across core business functions, growth support, administrative delivery, and specialized operational services.
          </p>
        </div>

        {/* Service Categories */}
        {serviceCategories.map((category, catIdx) => {
          const categoryServices = services.filter(s => s.category === category.id);
          return (
            <div key={category.id} className={`mb-8 ${catIdx > 0 ? 'pt-8 border-t border-gray-200' : ''}`}>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-2">{category.title}</h2>
              <p className="text-[#475569] mb-8">{category.description}</p>
              <div className={`grid grid-cols-1 md:grid-cols-2 ${categoryServices.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-2 max-w-3xl'} gap-6`}>
                {categoryServices.map(service => (
                  <Link key={service.id} to={service.path} className="card p-6" data-testid={`services-page-card-${service.id}`}>
                    <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center mb-4">
                      <service.icon className="w-6 h-6 text-[#2F80ED]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0B1F3A] mb-2">{service.title}</h3>
                    <p className="text-[#475569] text-sm mb-4">{service.shortDesc}</p>
                    <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1 text-sm">
                      Learn more <ChevronRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
    <CTABanner title="Ready to Scale Your Operations?" />
  </div>
);

// Individual Service Page
const ServicePage = ({ serviceId }) => {
  const service = services.find(s => s.id === serviceId);
  if (!service) return <div>Service not found</div>;

  const seoData = serviceContent[serviceId];
  const seoImages = serviceImages[serviceId] || [];

  return (
    <div data-testid={`service-page-${serviceId}`}>
      <SEOHead 
        title={service.seoTitle}
        description={service.seoDescription}
      />
      
      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <p className="overline text-[#2F80ED] mb-4">Services</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{service.title}</h1>
            <p className="text-xl text-gray-300 mb-8">{service.fullDesc}</p>
            <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="service-cta-btn">
              {service.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Introduction */}
      {seoData && (
        <section className="section">
          <div className="container mx-auto">
            <div className="max-w-4xl">
              <p className="text-lg text-[#475569] leading-relaxed">{seoData.intro}</p>
            </div>
            {seoImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 ">
                {seoImages.slice(0, 2).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.url} 
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-64 object-cover rounded-xl"
                    data-testid={`service-seo-img-${serviceId}-${idx}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* What We Do */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Capabilities</h2>
              <div className="space-y-4">
                {service.capabilities.map((cap, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#2F80ED]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#2F80ED]" />
                    </div>
                    <span className="text-[#475569]">{cap}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Benefits</h2>
              <div className="space-y-4">
                {service.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-[#475569]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Extended SEO Content Sections */}
      {seoData && seoData.sections.map((section, idx) => (
        <React.Fragment key={idx}>
          <section className={`section ${idx % 2 === 0 ? '' : 'section-surface'}`}>
            <div className="container mx-auto">
              <div className="max-w-4xl">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-6">{section.heading}</h2>
                {section.content.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-[#475569] leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
              {/* Show remaining images distributed across sections */}
              {seoImages.length > 2 && idx === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 ">
                  {seoImages.slice(2, 4).map((img, imgIdx) => (
                    <img 
                      key={imgIdx}
                      src={img.url} 
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-56 object-cover rounded-xl"
                      data-testid={`service-seo-img-${serviceId}-${imgIdx + 2}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </React.Fragment>
      ))}

      {/* Related Services */}
      <RelatedServices serviceId={serviceId} />

      {/* Internal Links */}
      <InternalLinks currentPage={`service-${serviceId}`} excludeServices={[serviceId]} />

      <CTABanner title={service.cta} />
    </div>
  );
};

// Industries Page
const IndustriesPage = () => (
  <div data-testid="industries-page">
    <SEOHead 
      title="Industries We Serve | APAC Center of Excellence"
      description="Specialized offshore teams for Technology, Staffing, and Financial Services companies. Tailored solutions for your industry's unique challenges."
    />
    
    <section className="section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">Expertise</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">Industries We Serve</h1>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto">
            We tailor our offshore teams to the specific needs of each industry we serve.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {industries.map(industry => (
            <Link key={industry.id} to={industry.path} className="card p-8" data-testid={`industries-page-card-${industry.id}`}>
              <div className="w-16 h-16 bg-[#0B1F3A] rounded-xl flex items-center justify-center mb-6">
                <industry.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0B1F3A] mb-4">{industry.title}</h3>
              <p className="text-[#475569] mb-4">Specialized support for {industry.title.toLowerCase()} companies.</p>
              <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CTABanner />
  </div>
);

// Individual Industry Page
const IndustryPage = ({ industryId }) => {
  const industry = industries.find(i => i.id === industryId);
  if (!industry) return <div>Industry not found</div>;

  const seoData = industryContent[industryId];
  const seoImages = industryImages[industryId] || [];

  const titles = {
    technology: 'Offshore Teams for Technology Companies',
    staffing: 'Offshore Support for Staffing Companies',
    'financial-services': 'Operational Support for Financial Services Firms'
  };

  return (
    <div data-testid={`industry-page-${industryId}`}>
      <SEOHead 
        title={`${industry.title} Industry | APAC Center of Excellence`}
        description={`Specialized offshore teams for ${industry.title.toLowerCase()} companies. Solutions for ${industry.challenges.join(', ').toLowerCase()}.`}
      />
      
      {/* Hero */}
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <p className="overline text-[#2F80ED] mb-4">Industries</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{titles[industryId]}</h1>
            <Link to="/contact?type=book_call" className="btn-primary bg-[#2F80ED]" data-testid="industry-cta-btn">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Introduction */}
      {seoData && (
        <section className="section">
          <div className="container mx-auto">
            <div className="max-w-4xl">
              <p className="text-lg text-[#475569] leading-relaxed">{seoData.intro}</p>
            </div>
            {seoImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 ">
                {seoImages.slice(0, 2).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.url} 
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-64 object-cover rounded-xl"
                    data-testid={`industry-seo-img-${industryId}-${idx}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Challenges & Solutions */}
      <section className="section section-surface">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Challenges</h2>
              <div className="space-y-4">
                {industry.challenges.map((challenge, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-[#475569]">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Our Solutions</h2>
              <div className="space-y-4">
                {industry.solutions.map((solution, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-[#475569]">{solution}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Extended SEO Content Sections */}
      {seoData && seoData.sections.map((section, idx) => (
        <React.Fragment key={idx}>
          <section className={`section ${idx % 2 === 0 ? '' : 'section-surface'}`}>
            <div className="container mx-auto">
              <div className="max-w-4xl">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-6">{section.heading}</h2>
                {section.content.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-[#475569] leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
              {seoImages.length > 2 && idx === 0 && (
                <div className="mt-5 ">
                  <img 
                    src={seoImages[2].url} 
                    alt={seoImages[2].alt}
                    loading="lazy"
                    className="w-full h-72 object-cover rounded-xl"
                    data-testid={`industry-seo-img-${industryId}-2`}
                  />
                </div>
              )}
            </div>
          </section>
        </React.Fragment>
      ))}

      {/* Internal Links */}
      <InternalLinks currentPage={`industry-${industryId}`} />

      <CTABanner title={`Scale Your ${industry.title} Team`} />
    </div>
  );
};

// How It Works Page
const HowItWorksPage = () => (
  <div data-testid="how-it-works-page">
    <SEOHead 
      title="How It Works | APAC Center of Excellence"
      description="Our proven 4-step process for building your offshore team: Define requirements, build your team, train & integrate, manage & optimize."
    />
    
    <section className="section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">The Process</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">How It Works</h1>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto">
            Our proven process ensures a smooth transition and successful offshore team deployment.
          </p>
        </div>
        <div className="max-w-4xl mx-auto space-y-6">
          {processSteps.map((step, idx) => (
            <div key={step.step} className="flex gap-8 items-start">
              <div className="w-16 h-16 bg-[#2F80ED] text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                {step.step}
              </div>
              <div className="flex-1 pt-2">
                <h3 className="text-2xl font-semibold text-[#0B1F3A] mb-3">{step.title}</h3>
                <p className="text-[#475569] text-lg">{step.description}</p>
                {idx < processSteps.length - 1 && (
                  <div className="h-12 w-px bg-gray-200 ml-8 mt-4" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    <CTABanner />
  </div>
);

// Pricing Page
const PricingPage = () => (
  <div data-testid="pricing-page">
    <SEOHead 
      title="Pricing | APAC Center of Excellence"
      description="Flexible pricing for offshore teams. Monthly per FTE, task-based pricing, or custom enterprise solutions. Reduce operating costs by up to 60%."
    />
    
    <section className="section">
      <div className="container mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-4">Pricing</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">Flexible Pricing</h1>
          <p className="text-lg text-[#475569] max-w-3xl mx-auto">
            Flexible pricing based on your needs. Reduce operating costs by up to 60% compared to US hiring.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div className="card p-8 text-center">
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-4">Monthly per FTE</h3>
            <p className="text-[#475569] mb-6">Predictable monthly cost per full-time equivalent team member.</p>
            <div className="text-3xl font-bold text-[#2F80ED] mb-6">Custom</div>
            <Link to="/contact?type=pricing" className="btn-outline w-full" data-testid="pricing-monthly-btn">Request Quote</Link>
          </div>
          <div className="card p-8 text-center border-2 border-[#2F80ED] relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2F80ED] text-white px-4 py-1 rounded-full text-sm font-medium">
              Popular
            </div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-4">Task-Based Pricing</h3>
            <p className="text-[#475569] mb-6">Pay based on tasks or projects completed. Perfect for variable workloads.</p>
            <div className="text-3xl font-bold text-[#2F80ED] mb-6">Flexible</div>
            <Link to="/contact?type=pricing" className="btn-primary w-full" data-testid="pricing-task-btn">Get Started</Link>
          </div>
          <div className="card p-8 text-center">
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-4">Custom Solutions</h3>
            <p className="text-[#475569] mb-6">Enterprise-grade solutions tailored to your specific requirements.</p>
            <div className="text-3xl font-bold text-[#2F80ED] mb-6">Enterprise</div>
            <Link to="/contact?type=pricing" className="btn-outline w-full" data-testid="pricing-enterprise-btn">Contact Sales</Link>
          </div>
        </div>
      </div>
    </section>
    
    <section className="section section-surface">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl font-bold text-[#0B1F3A] mb-8">Value You Can Count On</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8  mx-auto">
          <div>
            <div className="text-4xl font-bold text-[#2F80ED] mb-2">60%</div>
            <p className="text-[#475569]">Average cost reduction</p>
          </div>
          <div>
            <div className="text-4xl font-bold text-[#2F80ED] mb-2">30 Days</div>
            <p className="text-[#475569]">Team deployment</p>
          </div>
          <div>
            <div className="text-4xl font-bold text-[#2F80ED] mb-2">24/7</div>
            <p className="text-[#475569]">Support available</p>
          </div>
        </div>
      </div>
    </section>
    
    <CTABanner title="Request Custom Pricing" buttonText="Get Pricing" />
  </div>
);

// Case Studies Page
const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
      } catch (error) {
        console.error('Error fetching case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  return (
    <div data-testid="case-studies-page">
      <SEOHead 
        title="Case Studies | APAC Center of Excellence"
        description="See how we've helped companies transform their operations with offshore teams. Real results from real clients."
      />
      
      <section className="section">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <p className="overline mb-4">Results</p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">Case Studies</h1>
            <p className="text-lg text-[#475569] max-w-3xl mx-auto">
              See how we've helped companies transform their operations with offshore teams.
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : caseStudies.length === 0 ? (
            <div className="text-center py-12 text-[#475569]">No case studies available yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {caseStudies.map(cs => (
                <Link key={cs.id} to={`/case-studies/${cs.slug}`} className="card overflow-hidden" data-testid={`case-study-card-${cs.slug}`}>
                  {cs.image_url && (
                    <img src={cs.image_url} alt={cs.title} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6">
                    <span className="text-xs font-medium text-[#2F80ED] uppercase tracking-wide">{cs.industry}</span>
                    <h3 className="text-xl font-semibold text-[#0B1F3A] mt-2 mb-3">{cs.title}</h3>
                    <p className="text-[#475569] text-sm mb-4 line-clamp-2">{cs.problem}</p>
                    <span className="text-[#2F80ED] font-medium inline-flex items-center gap-1 text-sm">
                      Read more <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <CTABanner />
    </div>
  );
};

// Single Case Study Page
const CaseStudyDetailPage = ({ slug }) => {
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API}/case-studies/${slug}`);
        setCaseStudy(response.data);
      } catch (err) {
        setError('Case study not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) return <div className="section text-center">Loading...</div>;
  if (error) return <div className="section text-center text-red-500">{error}</div>;
  if (!caseStudy) return null;

  return (
    <div data-testid="case-study-detail-page">
      <SEOHead 
        title={`${caseStudy.title} | APAC Center of Excellence`}
        description={caseStudy.problem.substring(0, 160)}
      />
      
      <section className="section bg-[#0B1F3A] text-white">
        <div className="container mx-auto">
          <Link to="/case-studies" className="text-[#2F80ED] mb-4 inline-flex items-center gap-1 hover:underline">
            <ChevronRight className="w-4 h-4 rotate-180" /> Back to Case Studies
          </Link>
          <span className="block text-[#2F80ED] text-sm font-medium uppercase tracking-wide mt-4">{caseStudy.industry}</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2">{caseStudy.title}</h1>
        </div>
      </section>

      <section className="section">
        <div className="container mx-auto ">
          {caseStudy.image_url && (
            <img src={caseStudy.image_url} alt={caseStudy.title} className="w-full h-64 md:h-96 object-cover rounded-xl mb-6" />
          )}
          
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0B1F3A] mb-4">The Problem</h2>
              <p className="text-[#475569] text-lg leading-relaxed">{caseStudy.problem}</p>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-[#0B1F3A] mb-4">Our Solution</h2>
              <p className="text-[#475569] text-lg leading-relaxed">{caseStudy.solution}</p>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-[#0B1F3A] mb-4">Results</h2>
              <ul className="space-y-3">
                {caseStudy.results.map((result, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-[#475569] text-lg">{result}</span>
                  </li>
                ))}
              </ul>
            </div>

            {caseStudy.metrics && (
              <div className="bg-[#F7F9FC] rounded-xl p-8">
                <h3 className="text-xl font-semibold text-[#0B1F3A] mb-6">Key Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {Object.entries(caseStudy.metrics).map(([key, value]) => (
                    <div key={key}>
                      <div className="text-3xl font-bold text-[#2F80ED]">{value}</div>
                      <div className="text-[#475569] capitalize">{key.replace(/_/g, ' ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

// About Page - Updated
const AboutPage = () => (
  <div data-testid="about-page">
    <SEOHead 
      title="About APAC Centre of Excellence Pte Ltd | Singapore BPO Provider"
      description="APAC Centre of Excellence Pte Ltd is a Singapore-based business process outsourcing (BPO) company providing offshore staffing solutions with teams in the Philippines and Mexico. HR, finance, operations, and recruitment outsourcing."
    />
    
    <section className="section bg-[#0B1F3A] text-white">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="overline text-[#2F80ED] mb-4">About Us</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">APAC Centre of Excellence Pte Ltd</h1>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed">
              A Singapore-based business process outsourcing (BPO) company delivering dedicated offshore teams to businesses around the world.
            </p>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1758518727193-a8da31447858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBvZmZpY2V8ZW58MHx8fHwxNzc0OTIxNDI5fDA&ixlib=rb-4.1.0&q=85"
              alt="APAC Centre of Excellence offshore business process outsourcing team collaboration"
              className="rounded-xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Who We Are</h2>
        <p className="text-lg text-[#475569] mb-4 leading-relaxed">
          APAC Centre of Excellence Pte Ltd is a Singapore-registered company that provides business process outsourcing (BPO) capabilities to companies across the United States, Australia, and globally. We operate dedicated offshore delivery centres staffed by skilled professionals in the Philippines and Mexico, enabling our clients to access high-quality talent at significantly lower cost than onshore hiring.
        </p>
        <p className="text-lg text-[#475569] mb-4 leading-relaxed">
          Our outsourcing model goes beyond basic back-office support. We provide dedicated offshore staff who integrate directly into your business — working within your systems, following your processes, and operating as a seamless extension of your team. Whether you need Philippines-based staff for APAC-aligned operations or Mexico-based staff for nearshore US coverage, our delivery model is designed to match your business requirements.
        </p>
        <p className="text-lg text-[#475569] leading-relaxed">
          From HR operations and finance and accounting to recruitment process outsourcing (RPO), customer support, and specialised business functions, APAC Centre of Excellence helps growing companies build scalable, cost-effective operations without the complexity of managing offshore teams directly.
        </p>
      </div>
    </section>

    <section className="section section-surface">
      <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Our Offshore Delivery Model</h2>
        <p className="text-[#475569] mb-8 leading-relaxed">
          We provide dedicated offshore staff through our delivery centres in the Philippines and Mexico. Our staff are fully managed, trained, and supported — giving you the benefits of offshore outsourcing without the operational overhead of setting up your own entity abroad.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#2F80ED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-[#2F80ED]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-2">Dedicated Offshore Teams</h3>
            <p className="text-[#475569]">Your offshore staff in the Philippines or Mexico work exclusively for you, fully integrated with your processes and systems. No shared resources — 100% dedicated BPO staff.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-[#2F80ED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-[#2F80ED]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-2">Operational Expertise</h3>
            <p className="text-[#475569]">Deep experience across HR outsourcing, finance and accounting BPO, recruitment process outsourcing, and specialised business operations support.</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-[#2F80ED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-[#2F80ED]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-2">Scalable Outsourcing Model</h3>
            <p className="text-[#475569]">Scale your offshore team up or down as demand changes. Our BPO model gives you workforce flexibility without the long-term commitments of direct hiring.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container mx-auto">
        <h2 className="text-3xl font-bold text-[#0B1F3A] mb-6">Why Companies Choose Offshore BPO with APAC COE</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-3">Philippines-Based Staff</h3>
            <p className="text-[#475569] mb-6 leading-relaxed">
              The Philippines is one of the world's leading BPO destinations, with a large English-speaking workforce, strong cultural alignment with Western business practices, and deep talent pools across HR, finance, accounting, customer support, and administrative functions. Our Philippines-based staff deliver reliable, high-quality output across all service lines.
            </p>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-3">Mexico-Based Staff</h3>
            <p className="text-[#475569] leading-relaxed">
              For US companies seeking nearshore outsourcing with time-zone alignment, our Mexico-based staff provide an ideal solution. With bilingual capabilities, cultural proximity to the US market, and competitive cost structures, Mexican staff are particularly well-suited for customer-facing roles, sales support, and operations requiring real-time collaboration with US teams.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-3">Singapore Headquarters</h3>
            <p className="text-[#475569] mb-6 leading-relaxed">
              As a Singapore-based company, APAC Centre of Excellence Pte Ltd operates under one of the world's most respected business regulatory frameworks. Singapore's position as a global business hub provides our clients with confidence in corporate governance, intellectual property protection, and contractual reliability.
            </p>
            <h3 className="text-xl font-semibold text-[#0B1F3A] mb-3">Cost Savings of Up to 60%</h3>
            <p className="text-[#475569] leading-relaxed">
              Our offshore staffing model delivers significant cost reductions compared to onshore hiring — typically 40-60% savings across all roles. These savings come from lower labour costs in the Philippines and Mexico, combined with our managed delivery model that eliminates the overhead of setting up foreign operations independently.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Services Overview */}
    <section className="section section-surface">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl font-bold text-[#0B1F3A] mb-4">12 Outsourcing Services Across 4 Categories</h2>
        <p className="text-[#475569] mb-8 max-w-2xl mx-auto">
          From core business process outsourcing to specialised offshore support, we have the expertise to help you scale with dedicated Philippines and Mexico-based staff.
        </p>
        <Link to="/services" className="btn-primary" data-testid="about-view-services">
          View All Services <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>

    <CTABanner />
  </div>
);

// Contact Page - Updated with new service dropdown
const ContactPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const formType = params.get('type') || 'contact';

  const titles = {
    contact: 'Contact Us',
    book_call: 'Book a Call',
    pricing: 'Request Pricing'
  };

  return (
    <div data-testid="contact-page">
      <SEOHead 
        title={`${titles[formType]} | APAC Center of Excellence`}
        description="Ready to build your offshore team? Contact APAC Center of Excellence to discuss your needs and get started."
      />
      
      <section className="section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="overline mb-4">Get in Touch</p>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0B1F3A] mb-6">{titles[formType]}</h1>
              <p className="text-lg text-[#475569] mb-8">
                Ready to build your offshore team? Fill out the form and we'll get back to you within 24 hours.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#2F80ED]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#0B1F3A]">Email</div>
                    <a href="mailto:info@apaccoe.com" className="text-[#2F80ED]">info@apaccoe.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#2F80ED]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#0B1F3A]">Phone</div>
                    <span className="text-[#475569]">USA: +1 (844) 625-8400</span>
                    <br />
                    <span className="text-[#475569]">Australia: (08) 9930 0000</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#2F80ED]" />
                  </div>
                  <div>
                    <div className="font-medium text-[#0B1F3A]">Response Time</div>
                    <span className="text-[#475569]">Within 24 hours</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-8">
              <ContactForm formType={formType} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/admin/verify`, {
        auth: {
          username: credentials.username,
          password: credentials.password
        }
      });
      
      if (response.data.status === 'authenticated') {
        localStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`));
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F9FC]" data-testid="admin-login-page">
      <div className="card p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-[#0B1F3A] mb-6 text-center">Admin Login</h1>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-input"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              required
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              required
              data-testid="admin-password-input"
            />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-login-btn">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCS, setEditingCS] = useState(null);
  const [showCSForm, setShowCSForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const authHeader = localStorage.getItem('adminAuth');

  useEffect(() => {
    if (!authHeader) {
      navigate('/admin');
      return;
    }
    setIsAuthenticated(true);
    
    const axiosConfig = {
      headers: {
        'Authorization': `Basic ${authHeader}`
      }
    };
    
    const fetchData = async () => {
      try {
        const [leadsRes, csRes] = await Promise.all([
          axios.get(`${API}/admin/leads`, axiosConfig),
          axios.get(`${API}/admin/case-studies`, axiosConfig)
        ]);
        setLeads(leadsRes.data);
        setCaseStudies(csRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 401) {
          localStorage.removeItem('adminAuth');
          navigate('/admin');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authHeader, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const axiosConfig = {
    headers: {
      'Authorization': `Basic ${authHeader}`
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}?status=${status}`, {}, axiosConfig);
      setLeads(leads.map(l => l.id === leadId ? {...l, status} : l));
    } catch (err) {
      console.error('Error updating lead:', err);
    }
  };

  const deleteCS = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, axiosConfig);
      setCaseStudies(caseStudies.filter(cs => cs.id !== id));
    } catch (err) {
      console.error('Error deleting case study:', err);
    }
  };

  const handleCSSubmit = async (formData) => {
    try {
      if (editingCS) {
        const res = await axios.put(`${API}/admin/case-studies/${editingCS.id}`, formData, axiosConfig);
        setCaseStudies(caseStudies.map(cs => cs.id === editingCS.id ? res.data : cs));
      } else {
        const res = await axios.post(`${API}/admin/case-studies`, formData, axiosConfig);
        setCaseStudies([res.data, ...caseStudies]);
      }
      setShowCSForm(false);
      setEditingCS(null);
    } catch (err) {
      console.error('Error saving case study:', err);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-[#0B1F3A] text-white py-4 px-6">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-semibold">APAC COE Admin</h1>
          <button onClick={handleLogout} className="flex items-center gap-2 hover:text-[#2F80ED]" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="container mx-auto py-8 px-4">
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${activeTab === 'leads' ? 'bg-[#2F80ED] text-white' : 'bg-white text-[#0B1F3A]'}`}
            data-testid="admin-leads-tab"
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('case-studies')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${activeTab === 'case-studies' ? 'bg-[#2F80ED] text-white' : 'bg-white text-[#0B1F3A]'}`}
            data-testid="admin-case-studies-tab"
          >
            Case Studies ({caseStudies.length})
          </button>
        </div>

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F7F9FC]">
                  <tr>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Name</th>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Email</th>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Company</th>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Service</th>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Status</th>
                    <th className="text-left p-4 font-medium text-[#0B1F3A]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} className="border-t border-gray-100" data-testid={`lead-row-${lead.id}`}>
                      <td className="p-4">{lead.name}</td>
                      <td className="p-4">{lead.email}</td>
                      <td className="p-4">{lead.company || '-'}</td>
                      <td className="p-4">{lead.service_interest || '-'}</td>
                      <td className="p-4">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                          data-testid={`lead-status-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                        </select>
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length === 0 && (
                <div className="text-center py-12 text-gray-500">No leads yet</div>
              )}
            </div>
          </div>
        )}

        {/* Case Studies Tab */}
        {activeTab === 'case-studies' && (
          <div>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => { setEditingCS(null); setShowCSForm(true); }}
                className="btn-primary"
                data-testid="add-case-study-btn"
              >
                <Plus className="w-4 h-4" /> Add Case Study
              </button>
            </div>

            {showCSForm && (
              <CaseStudyForm
                caseStudy={editingCS}
                onSubmit={handleCSSubmit}
                onCancel={() => { setShowCSForm(false); setEditingCS(null); }}
              />
            )}

            <div className="space-y-4">
              {caseStudies.map(cs => (
                <div key={cs.id} className="card p-6" data-testid={`case-study-admin-${cs.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-[#2F80ED] uppercase">{cs.industry}</span>
                        {!cs.published && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Draft</span>}
                        {cs.featured && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Featured</span>}
                      </div>
                      <h3 className="text-xl font-semibold text-[#0B1F3A]">{cs.title}</h3>
                      <p className="text-gray-500 text-sm mt-1">/{cs.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingCS(cs); setShowCSForm(true); }}
                        className="p-2 hover:bg-gray-100 rounded"
                        data-testid={`edit-cs-${cs.id}`}
                      >
                        <Pencil className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => deleteCS(cs.id)}
                        className="p-2 hover:bg-red-50 rounded"
                        data-testid={`delete-cs-${cs.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Case Study Form Component
const CaseStudyForm = ({ caseStudy, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: caseStudy?.title || '',
    slug: caseStudy?.slug || '',
    industry: caseStudy?.industry || '',
    problem: caseStudy?.problem || '',
    solution: caseStudy?.solution || '',
    results: caseStudy?.results?.join('\n') || '',
    image_url: caseStudy?.image_url || '',
    featured: caseStudy?.featured || false,
    published: caseStudy?.published ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      results: formData.results.split('\n').filter(r => r.trim())
    });
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div className="card p-6 mb-6" data-testid="case-study-form">
      <h3 className="text-xl font-semibold text-[#0B1F3A] mb-4">
        {caseStudy ? 'Edit Case Study' : 'New Case Study'}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({
                ...formData, 
                title: e.target.value,
                slug: formData.slug || generateSlug(e.target.value)
              })}
              required
              data-testid="cs-title-input"
            />
          </div>
          <div>
            <label className="form-label">Slug *</label>
            <input
              type="text"
              className="form-input"
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              required
              data-testid="cs-slug-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Industry *</label>
            <select
              className="form-input"
              value={formData.industry}
              onChange={(e) => setFormData({...formData, industry: e.target.value})}
              required
              data-testid="cs-industry-select"
            >
              <option value="">Select industry</option>
              <option value="Technology">Technology</option>
              <option value="Staffing">Staffing</option>
              <option value="Financial Services">Financial Services</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="form-label">Image URL</label>
            <input
              type="url"
              className="form-input"
              value={formData.image_url}
              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://..."
              data-testid="cs-image-input"
            />
          </div>
        </div>
        <div>
          <label className="form-label">Problem *</label>
          <textarea
            className="form-input form-textarea"
            value={formData.problem}
            onChange={(e) => setFormData({...formData, problem: e.target.value})}
            required
            data-testid="cs-problem-textarea"
          />
        </div>
        <div>
          <label className="form-label">Solution *</label>
          <textarea
            className="form-input form-textarea"
            value={formData.solution}
            onChange={(e) => setFormData({...formData, solution: e.target.value})}
            required
            data-testid="cs-solution-textarea"
          />
        </div>
        <div>
          <label className="form-label">Results (one per line) *</label>
          <textarea
            className="form-input form-textarea"
            value={formData.results}
            onChange={(e) => setFormData({...formData, results: e.target.value})}
            placeholder="45% cost reduction&#10;2x processing capacity&#10;..."
            required
            data-testid="cs-results-textarea"
          />
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.published}
              onChange={(e) => setFormData({...formData, published: e.target.checked})}
              data-testid="cs-published-checkbox"
            />
            <span>Published</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => setFormData({...formData, featured: e.target.checked})}
              data-testid="cs-featured-checkbox"
            />
            <span>Featured</span>
          </label>
        </div>
        <div className="flex gap-4">
          <button type="submit" className="btn-primary" data-testid="cs-submit-btn">
            {caseStudy ? 'Update' : 'Create'} Case Study
          </button>
          <button type="button" onClick={onCancel} className="btn-secondary" data-testid="cs-cancel-btn">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ============== WRAPPER COMPONENTS ==============

// Wrapper for dynamic case study page
const CaseStudyDetailWrapper = () => {
  const location = useLocation();
  const slug = location.pathname.split('/').pop();
  return <CaseStudyDetailPage slug={slug} />;
};

// Wrapper for dynamic solution page
const SolutionPageWrapper = () => {
  const { slug } = useParams();
  return <SolutionPage slug={slug} />;
};

// Wrapper for dynamic location page
const LocationPageWrapper = () => {
  const { locationId } = useParams();
  return <LocationPage locationId={locationId} />;
};

// Wrapper for dynamic blog post
const BlogPostWrapper = () => {
  const { slug } = useParams();
  return <BlogPostPage slug={slug} />;
};

// ============== APP ==============

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Admin Routes (no navbar/footer) */}
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* Public Routes */}
        <Route path="/*" element={
          <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                {/* All 12 Service Pages */}
                <Route path="/services/hr" element={<ServicePage serviceId="hr" />} />
                <Route path="/services/finance" element={<ServicePage serviceId="finance" />} />
                <Route path="/services/operations" element={<ServicePage serviceId="operations" />} />
                <Route path="/services/customer-support" element={<ServicePage serviceId="customer-support" />} />
                <Route path="/services/recruitment" element={<ServicePage serviceId="recruitment" />} />
                <Route path="/services/sales-support" element={<ServicePage serviceId="sales-support" />} />
                <Route path="/services/executive-assistants" element={<ServicePage serviceId="executive-assistants" />} />
                <Route path="/services/data-services" element={<ServicePage serviceId="data-services" />} />
                <Route path="/services/fpa" element={<ServicePage serviceId="fpa" />} />
                <Route path="/services/compliance-support" element={<ServicePage serviceId="compliance-support" />} />
                <Route path="/services/project-management" element={<ServicePage serviceId="project-management" />} />
                <Route path="/services/process-optimization" element={<ServicePage serviceId="process-optimization" />} />
                {/* Industries */}
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/industries/technology" element={<IndustryPage industryId="technology" />} />
                <Route path="/industries/staffing" element={<IndustryPage industryId="staffing" />} />
                <Route path="/industries/financial-services" element={<IndustryPage industryId="financial-services" />} />
                {/* Other Pages */}
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/case-studies" element={<CaseStudiesPage />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetailWrapper />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogPostWrapper />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                {/* SEO Pages - Solutions */}
                <Route path="/solutions" element={<SolutionsIndexPage />} />
                <Route path="/solutions/:slug" element={<SolutionPageWrapper />} />
                {/* SEO Pages - Locations */}
                <Route path="/locations" element={<LocationsIndexPage />} />
                <Route path="/locations/:locationId" element={<LocationPageWrapper />} />
              </Routes>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
